import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { QrCode, Download, Send, ArrowLeft, CheckCircle2, MapPin, Phone, Store, User, CreditCard, FileText, Navigation, Hash, X, Ban } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChallanPreviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [challan, setChallan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    const fetchChallan = async () => {
      try {
        const { data } = await axios.get(`${API}/challans/${id}`, { withCredentials: true });
        setChallan(data);
      } catch {
        toast.error('Failed to load challan');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchChallan();
  }, [id, navigate]);

  const handlePayment = async () => {
    setProcessing(true);
    await new Promise(r => setTimeout(r, 2000));
    try {
      const { data } = await axios.put(`${API}/challans/${id}/payment`, {}, { withCredentials: true });
      setChallan(data);
      setShowPayment(false);
      toast.success('Payment received successfully!');
    } catch {
      toast.error('Payment processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/challans/${id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `challan_${challan.challan_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const handleNotify = async () => {
    setNotifying(true);
    try {
      await axios.post(`${API}/challans/${id}/notify`, {}, { withCredentials: true });
      toast.success(`WhatsApp & SMS sent to +91${challan.mobile_number} (MOCK)`);
    } catch {
      toast.error('Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse text-slate-400">Loading challan...</div>
    </div>
  );

  if (!challan) return null;

  const isPaid = challan.payment_status === 'paid';

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-3xl mx-auto animate-fade-in" data-testid="challan-preview-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 mb-4 sm:mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>
            Challan Preview
          </h1>
          <p className="text-slate-500 font-mono text-xs sm:text-sm mt-1" data-testid="challan-number">{challan.challan_number}</p>
        </div>
        <Badge
          data-testid="payment-status-badge"
          className={`text-xs sm:text-sm px-3 sm:px-4 py-1 sm:py-1.5 self-start ${isPaid
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}
          variant="outline"
        >
          {isPaid ? 'PAID' : 'UNPAID'}
        </Badge>
      </div>

      {/* Challan Card */}
      <div className="border border-slate-200 rounded-lg bg-white divide-y divide-slate-100" data-testid="challan-details-card">
        <div className="p-4 sm:p-5 bg-blue-600 text-white rounded-t-lg">
          <div className="text-center">
            <p className="text-blue-100 text-xs sm:text-sm font-medium uppercase tracking-widest">MuniCop - Municipal Corporation</p>
            <h2 className="text-base sm:text-xl font-bold mt-1" style={{ fontFamily: 'Outfit' }}>Offense Challan / Penalty Notice</h2>
          </div>
        </div>

        <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-y-3 sm:gap-y-4 gap-x-6 sm:gap-x-8">
          {challan.business_id && (
            <DetailRow icon={<Hash className="w-4 h-4" />} label="Business ID" value={challan.business_id} testId="detail-business-id" />
          )}
          <DetailRow icon={<Store className="w-4 h-4" />} label="Business Name" value={challan.business_name} testId="detail-business" />
          <DetailRow icon={<User className="w-4 h-4" />} label="Owner Name" value={challan.owner_name} testId="detail-owner" />
          <DetailRow icon={<Phone className="w-4 h-4" />} label="Mobile" value={`+91 ${challan.mobile_number}`} testId="detail-mobile" />
          <DetailRow icon={<FileText className="w-4 h-4" />} label="Business Type" value={challan.business_type} testId="detail-type" />
          <DetailRow icon={<FileText className="w-4 h-4" />} label="FSSAI Code" value={challan.fssai_code || 'N/A'} testId="detail-fssai" />
          <DetailRow icon={<MapPin className="w-4 h-4" />} label="GPS Location" value={`${challan.latitude?.toFixed(6)}, ${challan.longitude?.toFixed(6)}`} testId="detail-location" />
          {challan.address && (
            <div className="sm:col-span-2">
              <DetailRow icon={<Navigation className="w-4 h-4" />} label="Cart Address" value={challan.address} testId="detail-address" />
            </div>
          )}
        </div>

        {/* Photos with click-to-enlarge */}
        {challan.photo_paths && challan.photo_paths.length > 0 && (
          <div className="p-4 sm:p-5" data-testid="challan-photos">
            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-3">Photo Evidence (tap to enlarge)</p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {challan.photo_paths.map((path, idx) => (
                <PhotoThumb key={idx} path={path} idx={idx} onEnlarge={setLightboxSrc} />
              ))}
            </div>
          </div>
        )}

        <div className="p-4 sm:p-5 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-600">
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-semibold text-sm sm:text-base">Penalty Amount</span>
            </div>
            <span className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="challan-amount">
              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(challan.amount)}
            </span>
          </div>
        </div>

        <div className="p-4 sm:p-5 text-xs sm:text-sm text-slate-500">
          <p>Issued by: <span className="font-medium text-slate-700">{challan.officer_name}</span></p>
          <p className="mt-0.5">Date: <span className="font-medium text-slate-700">{challan.created_at ? new Date(challan.created_at).toLocaleString('en-IN') : 'N/A'}</span></p>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4 pb-8">
        {/* QR section: only show if NOT paid */}
        {!isPaid && !showPayment && (
          <Button
            onClick={() => setShowPayment(true)}
            data-testid="show-payment-btn"
            className="w-full h-11 sm:h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm sm:text-base tracking-wide transition-all duration-200"
          >
            <QrCode className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Proceed to Payment
          </Button>
        )}

        {showPayment && !isPaid && (
          <div className="border border-slate-200 rounded-lg p-4 sm:p-6 bg-white animate-fade-in" data-testid="payment-section">
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 text-center" style={{ fontFamily: 'Outfit' }}>
              Scan QR Code to Pay
            </h3>
            <div className="flex justify-center mb-4">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 sm:p-4 bg-slate-50">
                <img
                  src={`${API}/challans/${id}/qr`}
                  alt="Payment QR Code"
                  data-testid="payment-qr-code"
                  className="w-40 h-40 sm:w-48 sm:h-48 mx-auto"
                  crossOrigin="use-credentials"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
            <p className="text-xs sm:text-sm text-center text-slate-500 mb-1">Pay to: <span className="font-mono font-medium">municorp@hdfcbank</span></p>
            <p className="text-base sm:text-lg font-bold text-center text-slate-900 mb-4">
              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(challan.amount)}
            </p>
            <Button
              onClick={handlePayment}
              disabled={processing}
              data-testid="mock-payment-btn"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all duration-200"
            >
              {processing ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing Payment...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Simulate Payment Success
                </span>
              )}
            </Button>
          </div>
        )}

        {isPaid && (
          <div className="space-y-3 animate-fade-in" data-testid="post-payment-actions">
            <div className="p-3 sm:p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 mx-auto mb-2" />
              <p className="font-semibold text-emerald-800 text-sm sm:text-base">Payment Received Successfully</p>
              <p className="text-xs sm:text-sm text-emerald-600 mt-1">Paid on {challan.paid_at ? new Date(challan.paid_at).toLocaleString('en-IN') : 'N/A'}</p>
            </div>

            {/* Deactivated QR notice */}
            <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg flex items-center gap-3" data-testid="qr-deactivated-notice">
              <Ban className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <p className="text-xs sm:text-sm text-slate-500">Payment QR code has been deactivated for this challan.</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={handleDownloadPDF}
                data-testid="download-pdf-btn"
                variant="outline"
                className="h-11 border-blue-200 text-blue-700 hover:bg-blue-50 font-semibold"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={handleNotify}
                disabled={notifying}
                data-testid="send-notification-btn"
                variant="outline"
                className="h-11 border-emerald-200 text-emerald-700 hover:bg-emerald-50 font-semibold"
              >
                {notifying ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send WhatsApp & SMS
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Image Lightbox Dialog */}
      <Dialog open={!!lightboxSrc} onOpenChange={() => setLightboxSrc(null)}>
        <DialogContent className="max-w-3xl p-2 sm:p-4 bg-black/95 border-none" data-testid="image-lightbox">
          {lightboxSrc && (
            <img
              src={lightboxSrc}
              alt="Evidence enlarged"
              className="w-full h-auto max-h-[80vh] object-contain rounded"
              data-testid="lightbox-image"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-2.5 sm:gap-3" data-testid={testId}>
      <span className="text-slate-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider font-medium">{label}</p>
        <p className="text-xs sm:text-sm text-slate-900 font-medium mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
}

function PhotoThumb({ path, idx, onEnlarge }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await axios.get(`${API}/files/${path}`, { withCredentials: true, responseType: 'blob' });
        if (!cancelled) setSrc(URL.createObjectURL(resp.data));
      } catch { /* silently fail */ }
    }
    load();
    return () => { cancelled = true; };
  }, [path]);

  return (
    <button
      type="button"
      onClick={() => src && onEnlarge(src)}
      className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border border-slate-200 overflow-hidden bg-slate-100 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all duration-200"
      data-testid={`challan-photo-${idx}`}
    >
      {src ? (
        <img src={src} alt={`Evidence ${idx + 1}`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">Loading...</div>
      )}
    </button>
  );
}
